import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { FileText, Search, Download } from "lucide-react";
import { toast } from "sonner";
import { Watermark, Eyebrow } from "../components/Botanical";

export default function COAs() {
  const [coas, setCoas] = useState([]);
  const [q, setQ] = useState("");
  const [size, setSize] = useState("");
  const [batch, setBatch] = useState("");

  const load = () => {
    const params = {};
    if (q) params.q = q;
    if (size) params.size = size;
    if (batch) params.batch = batch;
    api.get("/coas", { params }).then((r) => setCoas(r.data || []));
  };
  useEffect(load, [q, size, batch]);

  const download = async (id) => {
    try {
      const r = await api.get(`/coas/${id}/download`);
      window.open(r.data.url, "_blank");
    } catch {
      toast.error("Download unavailable — COA storage not yet configured.");
    }
  };

  return (
    <div className="relative overflow-x-clip" data-testid="coas-page">
    <Watermark opacity={0.25} height="h-[240px] sm:h-[280px]" testid="coas-watermark" />
    <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-16">
      <Eyebrow>Documentation</Eyebrow>
      <h1 className="font-serif text-5xl mt-2">Certificates of Analysis</h1>
      <p className="text-sm text-[#5E4D5A] mt-3 max-w-xl">Search our COA library by product, size, or batch/lot number. All documents are available for download.</p>

      <div className="mt-8 max-w-3xl flex gap-4 items-start rounded-2xl bg-white/70 border border-[#D8CDBC] p-5 sm:p-6" data-testid="coa-testing-note">
        <div className="text-sm text-[#5E4D5A] leading-relaxed">
          <p className="font-serif text-xl text-[#5A433B] mb-1">Independent testing reports are coming soon</p>
          <p>Our product documentation currently includes Certificates of Analysis (COAs) provided by our vendors. We are also awaiting the completion of our own independently commissioned testing and will continue adding those reports to our COA library as they become available.</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-3 mt-10 mb-10">
        <div className="flex items-center bg-white rounded-full px-5 py-3 border border-[#A8B59A]/30">
          <Search size={14} className="text-[#5E6B51] mr-3" />
          <input data-testid="coa-search" placeholder="Product name" value={q} onChange={(e) => setQ(e.target.value)} className="bg-transparent outline-none text-sm flex-1 placeholder:text-[#5E4D5A]/60" />
        </div>
        <input data-testid="coa-size" placeholder="Size (e.g., 5mg)" value={size} onChange={(e) => setSize(e.target.value)} className="bg-white rounded-full px-5 py-3 border border-[#A8B59A]/30 outline-none text-sm placeholder:text-[#5E4D5A]/60" />
        <input data-testid="coa-batch" placeholder="Batch / lot number" value={batch} onChange={(e) => setBatch(e.target.value)} className="bg-white rounded-full px-5 py-3 border border-[#A8B59A]/30 outline-none text-sm placeholder:text-[#5E4D5A]/60" />
      </div>

      {coas.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-[#A8B59A]/30 rounded-2xl" data-testid="coa-empty">
          <FileText size={40} className="text-[#5E6B51]/40 mx-auto mb-4" strokeWidth={1.2} />
          <p className="text-[#5E4D5A]">No COAs match your filters.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {coas.map((c) => (
            <div key={c.id} data-testid={`coa-${c.id}`} className="bg-white rounded-2xl border border-[#A8B59A]/20 p-6 hover:shadow-md transition-shadow">
              <FileText size={28} className="text-[#59394F] mb-4" strokeWidth={1.5} />
              <h3 className="font-serif text-lg leading-tight">{c.product_name}</h3>
              <p className="text-xs text-[#5E4D5A] mt-2">Batch {c.batch_number}{c.size ? ` · ${c.size}` : ""}</p>
              {c.testing_lab && <p className="text-xs text-[#5E4D5A] mt-1">Lab: {c.testing_lab}</p>}
              {c.report_date && <p className="text-xs text-[#5E4D5A] mt-1">Report: {c.report_date}</p>}
              <button onClick={() => download(c.id)} data-testid={`coa-download-${c.id}`} className="mt-5 inline-flex items-center gap-2 text-sm text-[#59394F] hover:text-[#432A3B] transition-colors">
                <Download size={14} /> Download
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
    </div>
  );
}
